import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAllSessions } from '../utils/sessionManager';

/**
 * ProgressViewPremium - Redesigned progress view with refined analytics display
 * Clean stats cards, elegant data visualization, professional presentation
 */
const ProgressViewPremium = ({
  companyIdSlug,
  conversationContext = {},
  capturedInsights = [],
  savedNotes = []
}) => {
  const [allSessions, setAllSessions] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalSessions: 0,
    totalMessages: 0,
    frameworksUsed: 0,
    insightsCaptured: 0
  });

  useEffect(() => {
    if (companyIdSlug) {
      const { sessions } = getAllSessions(companyIdSlug);
      setAllSessions(sessions || []);

      // Calculate analytics
      const totalSessions = sessions.length;
      const totalMessages = sessions.reduce((sum, s) => sum + (s.messageCount || 0), 0);
      const frameworksUsed = new Set(sessions.filter(s => s.framework).map(s => s.framework)).size;
      const insightsCaptured = sessions.reduce((sum, s) => sum + (s.insights?.length || 0), 0) + capturedInsights.length;

      setAnalytics({
        totalSessions,
        totalMessages,
        frameworksUsed,
        insightsCaptured
      });
    }
  }, [companyIdSlug, capturedInsights.length]);

  // Get unique frameworks explored
  const frameworksExplored = Array.from(new Set(
    allSessions
      .filter(s => s.frameworkName)
      .map(s => s.frameworkName)
  ));

  // Get last session
  const lastSession = allSessions.length > 0
    ? allSessions.sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt))[0]
    : null;

  const formatLongDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="mm-progress-premium">
      {/* Header */}
      <div className="mm-view-header">
        <h2 className="mm-view-title">Your Progress</h2>
        <p className="mm-view-count">Track your coaching journey</p>
      </div>

      {/* Stats Grid */}
      <div className="mm-stats-grid">
        <motion.div className="mm-stat-card">
          <div className="mm-stat-icon">💬</div>
          <div className="mm-stat-value">{analytics.totalSessions}</div>
          <div className="mm-stat-label">Sessions</div>
        </motion.div>

        <motion.div className="mm-stat-card">
          <div className="mm-stat-icon">💡</div>
          <div className="mm-stat-value">{analytics.insightsCaptured}</div>
          <div className="mm-stat-label">Insights</div>
        </motion.div>

        <motion.div className="mm-stat-card">
          <div className="mm-stat-icon">🎯</div>
          <div className="mm-stat-value">{analytics.frameworksUsed}</div>
          <div className="mm-stat-label">Frameworks</div>
        </motion.div>

        <motion.div className="mm-stat-card">
          <div className="mm-stat-icon">📊</div>
          <div className="mm-stat-value">{analytics.totalMessages}</div>
          <div className="mm-stat-label">Messages</div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      {lastSession && (
        <div className="mm-section-header">
          <h3 className="mm-section-title">Recent Activity</h3>
        </div>
      )}

      {lastSession && (
        <div className="mm-activity-card">
          <div className="mm-activity-header">
            <div className="mm-activity-icon">📈</div>
            <div className="mm-activity-title">Last Session</div>
          </div>
          <div className="mm-activity-value">
            {formatLongDate(lastSession.updatedAt || lastSession.createdAt)}
          </div>
        </div>
      )}

      {/* Frameworks Explored */}
      {frameworksExplored.length > 0 && (
        <>
          <div className="mm-section-header">
            <h3 className="mm-section-title">Frameworks Explored</h3>
          </div>

          <div className="mm-frameworks-explored">
            {frameworksExplored.map((framework, index) => (
              <motion.span
                key={index}
                className="mm-framework-pill"
              >
                {framework}
              </motion.span>
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {analytics.totalSessions === 0 && (
        <div className="mm-empty-state">
          <div className="mm-empty-icon">📊</div>
          <h3 className="mm-empty-title">No progress yet</h3>
          <p className="mm-empty-description">
            Start coaching sessions to track your growth and insights here
          </p>
        </div>
      )}
    </div>
  );
};

export default ProgressViewPremium;
